import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAppTheme } from "../../../hooks/useAppTheme";
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  StyleSheet,
  ScrollView,
} from "react-native";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../../components/shared/Spinner";
import { Controller, useForm } from "react-hook-form";
import {
  getChinaShipmentsByContainerNo,
  getMergedTrackerOrders,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useTranslation } from "../../../utils/useTranslation";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { addRecentSearchedContainer } from "../../../redux/reducers/chinaTrackerReducer";

interface GenerateShipmentModalProps {
  visible: boolean;
  onClose: () => void;
  setSelectedEnquiryType: any;
}

const ChinaShipmentContainerSearchModal: React.FC<
  GenerateShipmentModalProps
> = ({ visible, onClose, setSelectedEnquiryType }) => {
  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);

  const { loadingChinaShipments, loading } = useSelector(
    (state: any) => state.chinaTracker.shipments,
  );
  const allOrders = useSelector((state: any) => state.chinaTracker.allOrders);
  const isChinaTrackerLoaded = useSelector(
    (state: any) => state.chinaTracker.isChinaTrackerLoaded,
  );
  const recentSearchedContainers = useSelector(
    (state: any) => state.chinaTracker.recentSearchedContainers,
  );
  const { t } = useTranslation();

  const dispatch = useDispatch<any>();
  const fetchTrackerOrders = () => {
    dispatch(getMergedTrackerOrders({ refresh: false }));
  };
  useEffect(() => {
    if (!isChinaTrackerLoaded) {
      fetchTrackerOrders();
    }
  }, [isChinaTrackerLoaded]);

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      containerNo: "",
    },
  });

  const watchedContainerNo = watch("containerNo");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const dropdownRef = useRef<any>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 400);
    return () => clearTimeout(timer);
  }, [visible]);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    if (showSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  // Extract unique container numbers from tracker orders, sorted by most recent
  const recentContainers = useMemo(() => {
    if (!allOrders || !Array.isArray(allOrders)) return [];
    
    const allShipments: any[] = [];
    for (const order of allOrders) {
      if (order.details && Array.isArray(order.details)) {
        for (const detail of order.details) {
          if (detail.shipments && Array.isArray(detail.shipments)) {
            for (const shipment of detail.shipments) {
              allShipments.push(shipment);
            }
          }
        }
      }
    }

    // Sort by createdAt desc
    allShipments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const seen = new Set<string>();
    const result: Array<{ containerNo: string; createdAt: string }> = [];
    for (const s of allShipments) {
      if (s.containerNo && !seen.has(s.containerNo)) {
        seen.add(s.containerNo);
        result.push({ containerNo: s.containerNo, createdAt: s.createdAt });
      }
    }
    return result;
  }, [allOrders]);

  const mergedRecentContainers = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ containerNo: string; createdAt: string }> = [];
    
    // Local searches take precedence (most recent)
    for (const s of (recentSearchedContainers || [])) {
      if (!seen.has(s.containerNo)) {
        seen.add(s.containerNo);
        result.push(s);
      }
    }
    
    // Then add tracker order containers
    for (const s of recentContainers) {
      if (!seen.has(s.containerNo)) {
        seen.add(s.containerNo);
        result.push(s);
      }
    }
    
    // Sort by createdAt desc
    result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return result;
  }, [recentSearchedContainers, recentContainers]);

  // Filter suggestions based on current input
  const filteredSuggestions = useMemo(() => {
    if (!watchedContainerNo || watchedContainerNo.trim() === "") {
      return mergedRecentContainers.slice(0, 8); // Show 8 most recent when empty
    }
    const query = watchedContainerNo.toLowerCase();
    return mergedRecentContainers.filter((c) =>
      c.containerNo.toLowerCase().includes(query),
    ).slice(0, 8);
  }, [watchedContainerNo, mergedRecentContainers]);

  const onSubmit = (formData: any) => {
    dispatch(getChinaShipmentsByContainerNo(formData.containerNo))
      .unwrap()
      .then(() => {
        // Save to Redux recent containers
        dispatch(addRecentSearchedContainer({ containerNo: formData.containerNo, createdAt: new Date().toISOString() }));

        setSelectedEnquiryType("generate_shipment_form");
        // }, 300);
      })
      .catch((error: any) => {
        // Properly format the error for react-hook-form
        setError("containerNo", {
          type: "server",
          message: error || "Container not found. Please try again.",
        });
      });
  };

  const selectSuggestion = (containerNo: string) => {
    setValue("containerNo", containerNo);
    setShowSuggestions(false);
  };

  const formatTimeAgo = (dateStr: string) => {
    if (!dateStr) return "";
    const diff = Math.floor(
      (Date.now() - new Date(dateStr).getTime()) / 1000,
    );
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
    });
  };

  // Highlight the matching portion of the text
  const highlightMatch = (text: string, query: string) => {
    if (!query || query.trim() === "") return <Text>{text}</Text>;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return <Text>{text}</Text>;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + query.length);
    const after = text.slice(idx + query.length);
    return (
      <Text>
        {before}
        <Text style={{ fontWeight: "700", color: themeColors.text }}>{match}</Text>
        {after}
      </Text>
    );
  };

  const { isSmallDevice, isMediumDevice } = useContext(
    DeviceOrientationContext,
  );

  const getModalWidth = () => {
    if (isSmallDevice) return "95%";
    if (isMediumDevice) return "80%";
    return "65%";
  };

  return (
    <>
      <Modal visible={visible} transparent animationType="fade">
        <View style={styles.overlay}>
          <View style={[styles.modalContainer, { width: getModalWidth() }]}>
            <Text style={styles.title}>{t("generateShipment")}</Text>

            {loadingChinaShipments || loading || !isChinaTrackerLoaded ? (
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            ) : (
              <>
                <View style={styles.inputRow}>
                  <Text style={styles.label}>{t("containerNo")}</Text>

                  <Controller
                    control={control}
                    name="containerNo"
                    rules={{ required: "Container No is required" }}
                    render={({ field: { onChange, value } }) => (
                      <View ref={dropdownRef} style={{ position: "relative", zIndex: 10 }}>
                        <TouchableWithoutFeedback onPress={() => setShowSuggestions(true)}>
                          <View style={styles.inputWrapper}>
                            <MaterialCommunityIcons
                              name="magnify"
                              size={18}
                              color={themeColors.textSecondary}
                              style={styles.searchIcon}
                            />
                            <TextInput
                              value={value}
                              onChangeText={(text) => {
                                onChange(text);
                              }}
                              onFocus={() => {
                                if (!isInitialMount.current) {
                                  setShowSuggestions(true);
                                  // Quietly refresh to pull any newly generated shipments
                                  dispatch(getMergedTrackerOrders({ refresh: true, showLoading: false }));
                                }
                              }}
                              style={[
                                styles.input,
                            // Highlight border red when there's an error
                                errors.containerNo && styles.inputError,
                              ]}
                              placeholder={t("shipmentSearchPlaceholder")}
                              placeholderTextColor={themeColors.textSecondary}
                            />
                            {value ? (
                              <TouchableOpacity
                                onPress={() => {
                                  onChange("");
                                  setShowSuggestions(false);
                                }}
                                style={styles.clearButton}
                              >
                                <MaterialCommunityIcons
                                  name="close-circle"
                                  size={16}
                                  color={themeColors.textSecondary}
                                />
                              </TouchableOpacity>
                            ) : null}
                          </View>
                        </TouchableWithoutFeedback>

                        {/* Suggestions dropdown */}
                        {showSuggestions && filteredSuggestions.length > 0 && (
                          <View style={styles.suggestionsContainer}>
                            <View style={styles.suggestionsHeader}>
                              <MaterialCommunityIcons
                                name="history"
                                size={14}
                                color="#6B7280"
                              />
                              <Text style={styles.suggestionsTitle}>
                                {watchedContainerNo?.trim()
                                  ? "Matching Containers"
                                  : "Recent Containers"}
                              </Text>
                            </View>
                            <ScrollView
                              style={styles.suggestionsList}
                              keyboardShouldPersistTaps="handled"
                              nestedScrollEnabled
                            >
                              {filteredSuggestions.map((item, idx) => (
                                <TouchableOpacity
                                  key={`${item.containerNo}-${idx}`}
                                  style={[
                                    styles.suggestionItem,
                                    idx === filteredSuggestions.length - 1 &&
                                      styles.suggestionItemLast,
                                  ]}
                                  onPress={() =>
                                    selectSuggestion(item.containerNo)
                                  }
                                >
                                  <View style={styles.suggestionLeft}>
                                    <MaterialCommunityIcons
                                      name="package-variant-closed"
                                      size={16}
                                      color="#6B7280"
                                      style={{ marginRight: 10 }}
                                    />
                                    <Text style={styles.suggestionText}>
                                      {highlightMatch(
                                        item.containerNo,
                                        watchedContainerNo,
                                      )}
                                    </Text>
                                  </View>
                                  <Text style={styles.suggestionTime}>
                                    {formatTimeAgo(item.createdAt)}
                                  </Text>
                                </TouchableOpacity>
                              ))}
                            </ScrollView>
                          </View>
                        )}

                        {/* No results message */}
                        {showSuggestions &&
                          watchedContainerNo?.trim() !== "" &&
                          filteredSuggestions.length === 0 && (
                            <View style={styles.suggestionsContainer}>
                              <View style={styles.noResultsRow}>
                                <MaterialCommunityIcons
                                  name="magnify"
                                  size={16}
                                  color={themeColors.textSecondary}
                                />
                                <Text style={styles.noResultsText}>
                                  Press Enter or Search to look up "{watchedContainerNo}"
                                </Text>
                              </View>
                            </View>
                          )}
                      </View>
                    )}
                  />

                  {/* Error renders dynamically as soon as errors.containerNo is set */}
                  {errors.containerNo && (
                    <Text style={styles.errorText}>
                      {errors.containerNo.message as string}
                    </Text>
                  )}
                </View>

                <View style={styles.footer}>
                  <TouchableOpacity
                    style={styles.closeButton}
                    onPress={onClose}
                  >
                    <Text style={styles.closeText}>{t("close")}</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.searchButton}
                    onPress={handleSubmit(onSubmit)}
                  >
                    <MaterialCommunityIcons
                      name="magnify"
                      size={16}
                      color="#FFFFFF"
                      style={{ marginRight: 4 }}
                    />
                    <Text style={styles.searchText}>{t("search")}</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>
    </>
  );
};

export default ChinaShipmentContainerSearchModal;

function createStyles(colors: any) {
  return StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.2)",
    overflow: "visible",
  } as any,
  modalContainer: {
    backgroundColor: colors.surfaceSubtle,
    borderRadius: 8,
    padding: 20,
    elevation: 5,
    overflow: "visible",
  } as any,
  title: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 20,
    color: colors.text,
  },
  inputRow: {
    backgroundColor: colors.surface,
    padding: 15,
    borderRadius: 6,
    marginBottom: 25,
    zIndex: 10,
    overflow: "visible",
  } as any,
  label: {
    fontSize: 16,
    marginBottom: 8,
    color: colors.text,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.surfaceSubtle,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchIcon: {
    paddingLeft: 10,
  },
  input: {
    flex: 1,
    height: 40,
    paddingHorizontal: 8,
    backgroundColor: "transparent",
    borderWidth: 0,
    color: colors.text,
  } as any,
  inputError: {
    borderColor: "red",
    borderWidth: 1.5,
  },
  clearButton: {
    paddingRight: 10,
    paddingLeft: 4,
  },

  // Suggestions dropdown
  suggestionsContainer: {
    position: "absolute",
    top: 44,
    left: 0,
    right: 0,
    backgroundColor: colors.surface,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: colors.text,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 6,
    zIndex: 100,
    overflow: "hidden",
  } as any,
  suggestionsHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.surfaceSubtle,
  },
  suggestionsTitle: {
    fontSize: 11,
    fontWeight: "600",
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  suggestionsList: {
    maxHeight: 220,
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  suggestionItemLast: {
    borderBottomWidth: 0,
  },
  suggestionLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  suggestionText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  suggestionTime: {
    fontSize: 11,
    color: colors.textSecondary,
    marginLeft: 8,
  },
  noResultsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 14,
  },
  noResultsText: {
    fontSize: 13,
    color: colors.textSecondary,
    fontStyle: "italic",
  },

  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    borderWidth: 1,
    borderColor: colors.textSecondary,
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: colors.border,
  },
  closeText: {
    fontSize: 14,
    color: colors.text,
  },
  searchButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#5aa9e6",
    paddingHorizontal: 25,
    paddingVertical: 10,
    borderRadius: 6,
  },
  searchText: {
    color: "#FFFFFF",
    fontWeight: "bold",
  },
  errorText: {
    color: "red",
    fontSize: 12,
    marginTop: 5,
  },
});
}

